import {
  AddTeamMemberFnReq,
  AddTeamMemberFnRes,
  AddTeamMemberStatus,
  Album,
  AlbumDto,
  Mapper,
  SharingResult,
  SharingResultDto,
  User,
} from '@uploadcam/common';
import { Functions, httpsCallable } from 'firebase/functions';
import { GetUserByIdInteractor } from '../../../auth/domain/interactors/get-user-by-id.interactor';
import { GetAuthDtoInteractor } from '../../../auth/domain/interactors/get-auth-dto.interactor';

interface AddTeamMemberErrorResult {
  $type: 'error';
  status: AddTeamMemberStatus;
}

interface AddTeamMemberWithoutDefaultAlbumResult {
  $type: 'without-default-album';
  user: User;
}

interface AddTeamMemberWithDefaultAlbumResult {
  $type: 'with-default-album';
  album: Album;
  sharingResult: SharingResult;
  user: User;
}

type AddTeamMemberResult =
  | AddTeamMemberErrorResult
  | AddTeamMemberWithoutDefaultAlbumResult
  | AddTeamMemberWithDefaultAlbumResult;

export class AddTeamMemberInteractor {
  private readonly addTeamMemberFn = httpsCallable<AddTeamMemberFnReq, AddTeamMemberFnRes>(
    this.functions,
    'addTeamMember',
  );

  constructor(
    private readonly albumMapper: Mapper<AlbumDto, Album>,
    private readonly functions: Functions,
    private readonly getAuthDto: GetAuthDtoInteractor,
    private readonly getUserById: GetUserByIdInteractor,
    private readonly sharingResultMapper: Mapper<SharingResultDto, SharingResult>,
  ) {}

  public async execute(data: Omit<AddTeamMemberFnReq, 'auth'>): Promise<AddTeamMemberResult> {
    const res = await this.addTeamMemberFn({
      auth: await this.getAuthDto.execute(),
      ...data,
    });

    if (!res.data.added) {
      return {
        $type: 'error',
        status: res.data.status,
      };
    }

    const user = await this.getUserById.execute(res.data.userId);

    if (res.data.createAlbumResult?.status === 'created') {
      return {
        $type: 'with-default-album',
        user,
        album: this.albumMapper.map(res.data.createAlbumResult.album),
        sharingResult: this.sharingResultMapper.map(res.data.createAlbumResult.sharingResult),
      };
    } else {
      return { $type: 'without-default-album', user };
    }
  }
}
